import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
} from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { AlertComponent } from "@modules/core/components/alert/alert.component";
import { createDialogConfig } from "@utils";
import { Observable } from "rxjs";

/** Matches the backend limit for G2 review feedback. */
export const G2_REVIEW_FEEDBACK_MAX_LENGTH = 3200;

export interface G2ReviewFeedbackDialogData {
  /** Sends the trimmed feedback. The dialog stays open until it succeeds. */
  submitFeedback: (feedbackText: string) => Observable<unknown>;
}

/** `true` once feedback was sent, or `undefined` when the dialog is closed. */
export type G2ReviewFeedbackDialogResult = true | undefined;

@Component({
  selector: "app-g2-review-feedback-dialog",
  templateUrl: "./g2-review-feedback-dialog.component.html",
  styleUrls: ["./g2-review-feedback-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class G2ReviewFeedbackDialogComponent {
  public readonly maxLength = G2_REVIEW_FEEDBACK_MAX_LENGTH;
  public isSubmitting = false;
  public readonly feedback = new FormControl("", {
    nonNullable: true,
    validators: [Validators.maxLength(G2_REVIEW_FEEDBACK_MAX_LENGTH)],
  });

  public constructor(
    @Inject(MAT_DIALOG_DATA) private dialogData: G2ReviewFeedbackDialogData,
    private matDialogRef: MatDialogRef<
      G2ReviewFeedbackDialogComponent,
      G2ReviewFeedbackDialogResult
    >,
    private matDialog: MatDialog,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  public static openDialog(
    matDialog: MatDialog,
    data: G2ReviewFeedbackDialogData
  ): MatDialogRef<
    G2ReviewFeedbackDialogComponent,
    G2ReviewFeedbackDialogResult
  > {
    const config = createDialogConfig<G2ReviewFeedbackDialogData>(data, {
      panelClasses: ["g2-review-feedback-dialog"],
      width: "540px",
      maxWidth: "95vw",
    });
    // The app default focuses the dialog container, so target the field.
    config.autoFocus = "textarea";
    return matDialog.open<
      G2ReviewFeedbackDialogComponent,
      G2ReviewFeedbackDialogData,
      G2ReviewFeedbackDialogResult
    >(G2ReviewFeedbackDialogComponent, config);
  }

  public get canSubmit(): boolean {
    return (
      !this.isSubmitting &&
      this.feedback.valid &&
      this.feedback.value.trim().length > 0
    );
  }

  public submit(event?: Event): void {
    event?.preventDefault();
    if (!this.canSubmit) return;
    this.setSubmitting(true);

    this.dialogData.submitFeedback(this.feedback.value.trim()).subscribe({
      next: () => this.matDialogRef.close(true),
      error: () => {
        // The text stays in the field so the user can try again.
        AlertComponent.openErrorDialogFromErrorDescriptor(this.matDialog);
        this.setSubmitting(false);
      },
    });
  }

  private setSubmitting(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;
    this.matDialogRef.disableClose = isSubmitting;
    if (isSubmitting) {
      this.feedback.disable({ emitEvent: false });
    } else {
      this.feedback.enable({ emitEvent: false });
    }
    this.changeDetectorRef.markForCheck();
  }
}
